/**
 * TimePhoria variant swatch responsiveness patch.
 *
 * 1. Gives duplicated tabbed swatch inputs unique IDs so labels update the
 *    visible control immediately.
 * 2. Synchronizes the selected visual state before the theme's section fetch
 *    and gallery replacement complete.
 * 3. Switches the existing media gallery optimistically while price and stock
 *    continue updating from Shopify in the background.
 * 4. Warms variant hero images at low priority when the picker approaches the
 *    viewport, reducing visible image delay without blocking initial render.
 */
(() => {
  const READY_ATTRIBUTE = 'data-tp-variant-speed-ready';
  const PRELOADED = new Set();
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const shouldAvoidBackgroundPreload = Boolean(
    connection?.saveData || ['slow-2g', '2g'].includes(connection?.effectiveType)
  );

  const runWhenIdle = (callback, timeout = 1200) => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(callback, { timeout });
      return;
    }
    window.setTimeout(callback, Math.min(timeout, 500));
  };

  const variantImageUrl = (variant) => {
    const source =
      variant?.featured_media?.preview_image?.src ||
      variant?.featured_image?.src ||
      variant?.featured_image;

    if (!source || typeof source !== 'string') return null;

    const url = new URL(source, window.location.origin);
    url.searchParams.set('width', window.matchMedia('(max-width: 749px)').matches ? '640' : '980');
    return url.href;
  };

  const preloadImage = (url) => {
    if (!url || PRELOADED.has(url)) return;
    PRELOADED.add(url);

    const image = new Image();
    image.decoding = 'async';
    image.fetchPriority = 'low';
    image.src = url;
  };

  const readVariants = (picker) => {
    const json = picker.querySelector('[data-product-variants]')?.textContent;
    if (!json) return [];

    try {
      return JSON.parse(json);
    } catch (_error) {
      return [];
    }
  };

  const warmImagesInBatches = (picker) => {
    if (shouldAvoidBackgroundPreload || picker.dataset.tpImagesWarmed === 'true') return;
    picker.dataset.tpImagesWarmed = 'true';

    const urls = [...new Set(readVariants(picker).map(variantImageUrl).filter(Boolean))];
    let index = 0;

    const warmNextBatch = () => {
      urls.slice(index, index + 4).forEach(preloadImage);
      index += 4;
      if (index < urls.length) runWhenIdle(warmNextBatch, 1600);
    };

    runWhenIdle(warmNextBatch, 800);
  };

  const warmHoveredVariant = (picker, target) => {
    const input = target.closest?.('input[data-option-value-id], label');
    const optionInput = input?.matches('input')
      ? input
      : input?.control || (input?.htmlFor ? document.getElementById(input.htmlFor) : null);
    if (!optionInput?.dataset?.optionValueId) return;

    const optionPosition = Number(optionInput.name.trim().match(/-(\d+)$/)?.[1] || 1);
    const optionIndex = optionPosition - 1;
    const matchingVariants = readVariants(picker).filter(
      (item) => item?.options?.[optionIndex] === optionInput.value
    );
    const variant = matchingVariants.find((item) => item.available) || matchingVariants[0];
    preloadImage(variantImageUrl(variant));
  };

  const makeInputIdsUnique = (picker) => {
    picker.querySelectorAll('fieldset input[type="radio"]').forEach((input, index) => {
      const label = input.nextElementSibling?.matches('label') ? input.nextElementSibling : null;
      const optionId = input.dataset.optionValueId || input.value || index;
      const uniqueId = `${input.id || 'variant-option'}-tp-${optionId}-${index}`;

      input.id = uniqueId;
      if (label) label.htmlFor = uniqueId;
    });
  };

  const syncSelectedState = (picker, target) => {
    if (!target.matches?.('input[type="radio"][data-option-value-id]')) return;
    const selectedId = target.dataset.optionValueId;

    picker.querySelectorAll('input[type="radio"][data-option-value-id]').forEach((input) => {
      input.classList.toggle('active', input.dataset.optionValueId === selectedId);
    });
  };

  const findSelectedVariant = (picker, target) => {
    const selections = new Map();

    picker.querySelectorAll('select').forEach((select, index) => {
      selections.set(index + 1, select.value);
    });
    picker.querySelectorAll('fieldset input[type="radio"]:checked').forEach((input) => {
      const position = Number(input.name.trim().match(/-(\d+)$/)?.[1] || 1);
      selections.set(position, input.value);
    });

    const targetPosition = Number(target.name.trim().match(/-(\d+)$/)?.[1] || 1);
    selections.set(targetPosition, target.value);

    return readVariants(picker).find((variant) =>
      variant?.options?.every((value, index) => selections.get(index + 1) === value)
    );
  };

  const optimisticallyApplyVariant = (picker, target) => {
    const variant = findSelectedVariant(picker, target);
    const productInfo = picker.closest('product-info');
    if (!variant || !productInfo) return;

    productInfo.currentVariant = variant;
    productInfo.updateMedia?.(variant, true);
    productInfo.updateVariantInputs?.(variant.id);
  };

  const initializePicker = (picker) => {
    if (!(picker instanceof HTMLElement) || picker.hasAttribute(READY_ATTRIBUTE)) return;
    picker.setAttribute(READY_ATTRIBUTE, 'true');

    makeInputIdsUnique(picker);

    picker.addEventListener(
      'change',
      (event) => {
        syncSelectedState(picker, event.target);
        optimisticallyApplyVariant(picker, event.target);
      },
      { capture: true }
    );
    picker.addEventListener('pointerover', (event) => warmHoveredVariant(picker, event.target), {
      passive: true,
    });
    picker.addEventListener('pointerdown', (event) => warmHoveredVariant(picker, event.target), {
      passive: true,
    });
    picker.addEventListener('focusin', (event) => warmHoveredVariant(picker, event.target));

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          warmImagesInBatches(picker);
        },
        { rootMargin: '500px 0px' }
      );
      observer.observe(picker);
    } else {
      warmImagesInBatches(picker);
    }
  };

  const initializeWithin = (root) => {
    if (root.matches?.('variant-selects')) initializePicker(root);
    root.querySelectorAll?.('variant-selects').forEach(initializePicker);
  };

  initializeWithin(document);

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) initializeWithin(node);
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
